"use client";
import Image from "next/image";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import { ContentfulInspectorManager } from "@packages/lib/contentful-preview/ContentfulInspector";
import { logClickstreamEvent } from "@packages/lib/utlils/clickstream";
import { GA4DataLayer } from "@packages/lib/utlils/helper-function";
import { GAConstants } from "@packages/shared-components/services/constants";
import API_URL from "@packages/lib/server-actions/api-urls";

export default function Richtextcomponent({
  propsdata,
  key,
  preview,
  isSubjectGuide,
  subjectTextKey,
  pageName,
}: any) {
  const data = useContentfulLiveUpdates(propsdata);

  function trackRedirectionLinkClick(linkText: string, url: string) {
    GA4DataLayer({
      event: GAConstants.track_event,
      event_name: "redirection_link_click",
      click_text: linkText?.toLowerCase()?.replace(/\s+/g, "_"),
      field: url,
      page_section:
        data?.paragraphTitle?.toLowerCase()?.replace(/\s+/g, "_") ||
        "find_out_more",
      page_name: pageName || GAConstants.subjectGuideDetailsPageName,
      page_grouping: isSubjectGuide
        ? GAConstants.pageGrouping.subjectguide_details
        : GAConstants.pageGrouping.money_related,
      subject_area: subjectTextKey || "na",
      subject_discipline: subjectTextKey || "na",
    });
  }

  function handleRichTextVideoTracking() {
    logClickstreamEvent({
      sectionName: "rich text",
      pageName: localStorage.getItem("gaPageName"),
      eventType: "videoPlayed",
    });
  }

  const handleExternalLinkClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    url: string,
  ) => {
    e.preventDefault();
    const linkText = (e.currentTarget as HTMLAnchorElement).innerText?.trim();

    // Open URL immediately — no delay for user
    window.open(url, "_blank");

    // Resolve institution and log in background
    if (linkText) {
      try {
        //commenting this, because it's causing error in the API level
        const responseData: any = null;
        // const response = await fetch(
        //   `${process.env.NEXT_PUBLIC_DOMSERVICE_API_DOMAIN}/${API_URL?.resolveWebclick}`,
        //   {
        //     method: "POST",
        //     headers: {
        //       "Content-Type": "application/json",
        //       "x-api-key": `${process.env.NEXT_PUBLIC_DOMSERVICE_X_API_KEY}`,
        //       siteCode: process.env.PROJECT === "PGS" ? "PGS" : "WU",
        //     },
        //     body: JSON.stringify({ institutionName: linkText }),
        //   },
        // );
        // const responseData = await response.json();

        // GA4 revenue event
        // GA4DataLayer({
        //   event: GAConstants.revenue_kpi,
        //   event_name: "visit_website",
        //   institution_id: responseData?.collegeId
        //     ? String(responseData.collegeId)
        //     : "",
        //   institution_name: responseData?.institutionName || linkText,
        //   cta_url: url,
        //   page_name: localStorage.getItem("gaPageName") || "articleDetail",
        //   page_grouping: "money_related",
        //   provider_type:
        //     responseData?.isAdvertiser === "Y"
        //       ? "advertiser"
        //       : "non_advertiser",
        //   field: "article_in_content",
        //   click_text: "visit_website",
        // });

        // Clickstream for CPE delivery — only if advertiser
        if (responseData?.isAdvertiser === "Y") {
          logClickstreamEvent({
            eventType: "VisitWebsite",
            actionType: "Interaction",
            CTATitle: "Visit website",
            idpcInstitutionId: responseData.collegeId,
            subOrderItemId: responseData.subOrderItemId,
            orderItemId: responseData.orderItemId,
            externalUrl: url,
            isAdvertiser: "Y",
            pageName: localStorage.getItem("gaPageName") || "articleDetail",
            functionalityName: "Article",
            sectionName: "article_in_content",
          });
        }
      } catch {
        // Silent fail — don't block user experience
      } finally {
        // Always fire redirection GA4 event regardless of API success/failure
        trackRedirectionLinkClick(linkText, url);
      }
    }
  };

  const customOptions = {
    renderNode: {
      paragraph: (node: any, children: any) => <p>{children}</p>,
      "heading-1": (node: any, children: any) => <h1>{children}</h1>,
      "heading-2": (node: any, children: any) => <h2>{children}</h2>,
      "heading-3": (node: any, children: any) => <h3>{children}</h3>,
      "unordered-list": (node: any, children: any) => <ul>{children}</ul>,
      "ordered-list": (node: any, children: any) => <ol>{children}</ol>,
      "list-item": (node: any, children: any) => <li>{children}</li>,
      video: (node: any) => (
        <video
          width="100%"
          style={{ maxWidth: "600px", display: "block", margin: "0 auto" }}
          onClick={() => handleRichTextVideoTracking()}
        >
          <source src={node.data.uri} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      ),
      hyperlink: (node: any, children: any) => {
        const url = node.data.uri;
        const isInternal =
          url.includes("whatuni") || url.includes("postgraduatesearch");
        const linkText =
          node.content?.map((c: any) => c.value || "").join("") || "";

        if (isInternal) {
          // Internal link — subject guide / redirection tracking
          return (
            <a
              href={url}
              target="_self"
              onClick={() => {
                trackRedirectionLinkClick(linkText, url);
              }}
            >
              {children}
            </a>
          );
        }

        // External link — institution resolution + revenue KPI + CPE delivery
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e: any) => handleExternalLinkClick(e, url)}
          >
            {children}
          </a>
        );
      },
    },

    renderMark: {
      bold: (text: any) => <strong>{text}</strong>,
      italic: (text: any) => <em>{text}</em>,
      underline: (text: any) => <u>{text}</u>,
    },
    renderText: (text: any) => {
      return (
        text
          // Replace `<br>` with a special marker for processing
          .replace(/<br>/g, "__BR__")
          // Split on newlines or markers
          .split(/(\n\n|__BR__)/)
          .flatMap((part: any, index: any) => {
            if (part === "\n\n") {
              // Add an empty line for double newlines
              return [
                <br key={`br-double-${index}`} />,
                <br key={`br-empty-${index}`} />,
              ];
            } else if (part === "__BR__") {
              // Add a single line break for `<br>`
              return <br key={`br-${index}`} />;
            } else {
              // Regular text
              return part;
            }
          })
      );
    },
  };

  return (
    <>
      {preview && (
        <ContentfulInspectorManager
          fields={[
            {
              entryId: data?.sys?.id,
              fieldId: "paragraphTitle",
              targetSelector: "#artilce-page-paragraph-title",
            },
            {
              entryId: data?.sys?.id,
              fieldId: "media",
              targetSelector: "#artilce-page-media",
            },
            {
              entryId: data?.sys?.id,
              fieldId: "paragraphBodyRichText",
              targetSelector: `#${data?.skipLinkId}`,
            },
            {
              entryId: data?.sys?.id,
              fieldId: "paragraphBodyRichText",
              targetSelector: `#richtextjson${key}`,
            },
          ]}
        />
      )}
      <div id={data?.skipLinkId} className="rtf-innerstyle">
        {data?.paragraphTitle && (
          <h5 id="artilce-page-paragraph-title">{data?.paragraphTitle}</h5>
        )}
        {data?.componentStyle === "Standard" ? (
          <video src={data?.media?.url} controls />
        ) : (
          data?.media?.url && (
            <Image
              id="artilce-page-media"
              priority={true}
              alt="Image"
              src={data?.media?.url}
              width={700}
              height={700}
              className="mb-4 w-full"
            />
          )
        )}
        <div id={`richtextjson${key}`}>
          {data?.paragraphBodyRichText?.json &&
            documentToReactComponents(
              data?.paragraphBodyRichText.json,
              customOptions,
            )}
        </div>
        <div></div>
      </div>
    </>
  );
}
